// Room Type Registry — organized by site type per BMS-spec
// Reference: BMS-spec/docs/reference/space-profiles.md
// Hospitality room types (hotels, resorts)
const HOSPITALITY_TYPES = [
    { value: 'guest_room', label: 'Guest Room', domain: 'hospitality' },
    { value: 'guest_bathroom', label: 'Guest Bathroom', domain: 'hospitality' },
    { value: 'corridor', label: 'Corridor', domain: 'hospitality' },
    { value: 'lobby', label: 'Lobby', domain: 'hospitality' },
    { value: 'reception', label: 'Reception', domain: 'hospitality' },
    { value: 'restaurant', label: 'Restaurant', domain: 'hospitality' },
    { value: 'bar', label: 'Bar', domain: 'hospitality' },
    { value: 'conference_room', label: 'Conference Room', domain: 'hospitality' },
    { value: 'meeting_room', label: 'Meeting Room', domain: 'hospitality' },
    { value: 'gym', label: 'Gym', domain: 'hospitality' },
    { value: 'spa', label: 'Spa', domain: 'hospitality' },
    { value: 'pool_area', label: 'Pool Area', domain: 'hospitality' },
    { value: 'wellness', label: 'Wellness', domain: 'hospitality' },
    { value: 'staff_room', label: 'Staff Room', domain: 'hospitality' },
    { value: 'service_room', label: 'Service Room', domain: 'hospitality' },
    { value: 'wc_public', label: 'Public Restroom', domain: 'hospitality' },
];
// Residential room types (homes, apartments)
const RESIDENTIAL_TYPES = [
    { value: 'bedroom', label: 'Bedroom', domain: 'residential' },
    { value: 'living_room', label: 'Living Room', domain: 'residential' },
    { value: 'kitchen', label: 'Kitchen', domain: 'residential' },
    { value: 'bathroom', label: 'Bathroom', domain: 'residential' },
    { value: 'dining_room', label: 'Dining Room', domain: 'residential' },
    { value: 'laundry', label: 'Laundry', domain: 'residential' },
    { value: 'outlock_area', label: 'Outlock Area', domain: 'residential' },
    { value: 'terrace', label: 'Terrace', domain: 'residential' },
    { value: 'storage', label: 'Storage', domain: 'residential' },
];
// Commercial room types (offices, retail)
const COMMERCIAL_TYPES = [
    { value: 'office', label: 'Office', domain: 'commercial' },
    { value: 'backoffice', label: 'Back Office', domain: 'commercial' },
    { value: 'open_space', label: 'Open Space', domain: 'commercial' },
    { value: 'server_room', label: 'Server Room', domain: 'commercial' },
    { value: 'break_room', label: 'Break Room', domain: 'commercial' },
    { value: 'meeting_room', label: 'Meeting Room', domain: 'commercial' },
    { value: 'conference_room', label: 'Conference Room', domain: 'commercial' },
    { value: 'stairwell', label: 'Stairwell', domain: 'commercial' },
];
// Industrial room types (factories, warehouses)
const INDUSTRIAL_TYPES = [
    { value: 'plantroom', label: 'Plant Room', domain: 'industrial' },
    { value: 'roof_technical', label: 'Roof Technical', domain: 'industrial' },
    { value: 'workshop', label: 'Workshop', domain: 'industrial' },
    { value: 'loading_dock', label: 'Loading Dock', domain: 'industrial' },
    { value: 'warehouse', label: 'Warehouse', domain: 'industrial' },
    { value: 'control_room', label: 'Control Room', domain: 'industrial' },
];
// Shared room types (available in all domains)
const SHARED_TYPES = [
    { value: 'parking', label: 'Parking', domain: 'shared' },
    { value: 'technical_room', label: 'Technical Room', domain: 'shared' },
    { value: 'electrical_room', label: 'Electrical Room', domain: 'shared' },
    { value: 'storage', label: 'Storage', domain: 'shared' },
    { value: 'elevator_lobby', label: 'Elevator Lobby', domain: 'shared' },
];
// All room types grouped by domain
export const ROOM_TYPES_BY_DOMAIN = {
    hospitality: HOSPITALITY_TYPES,
    residential: RESIDENTIAL_TYPES,
    commercial: COMMERCIAL_TYPES,
    industrial: INDUSTRIAL_TYPES,
    shared: SHARED_TYPES,
};
/**
 * The translation key of a room type code: `roomTypes.guest_room`. The
 * `label`s above are the English reference; screens pass `t` to the functions
 * below so a reader sees their own language. Every code has a key in every
 * locale (`packages/common/src/i18n/index.test.ts`).
 */
export function roomTypeLabelKey(value) {
    return `roomTypes.${value}`;
}
/** One option per code (meeting rooms, storage appear in two domains), labelled and sorted. */
function localized(types, tr) {
    const byValue = new Map();
    for (const type of types) {
        if (!byValue.has(type.value))
            byValue.set(type.value, type);
    }
    return [...byValue.values()]
        .map((type) => (tr ? { ...type, label: tr(roomTypeLabelKey(type.value)) } : type))
        .sort((a, b) => a.label.localeCompare(b.label));
}
/**
 * Get room type options filtered by site type
 * Includes domain-specific types + shared types
 */
export function getRoomTypesForSiteType(siteType, tr) {
    const domainTypes = ROOM_TYPES_BY_DOMAIN[siteType] || [];
    return localized([...domainTypes, ...SHARED_TYPES], tr);
}
/**
 * Get all room types (for admin views without site context)
 */
export function getAllRoomTypes(tr) {
    return localized([
        ...HOSPITALITY_TYPES,
        ...RESIDENTIAL_TYPES,
        ...COMMERCIAL_TYPES,
        ...INDUSTRIAL_TYPES,
        ...SHARED_TYPES,
    ], tr);
}
/** "hotel_room" -> "Hotel room": a code outside the registry, made readable. */
function humanizeRoomType(value) {
    // Free text a site typed ("Standard double") is already readable.
    if (!/^[a-z0-9]+([_-][a-z0-9]+)*$/.test(value))
        return value;
    const words = value.replace(/[_\-\s]+/g, ' ').trim().toLowerCase();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : value;
}
/**
 * Get room type label by value; a code outside the registry (a vendor or a
 * future gateway adds one) is shown humanised, never as raw snake_case.
 */
export function getRoomTypeLabel(value, tr) {
    const found = getAllRoomTypes().find((t) => t.value === value);
    if (!found)
        return humanizeRoomType(value);
    return tr ? tr(roomTypeLabelKey(value)) : found.label;
}
//# sourceMappingURL=roomTypes.js.map